import {
  CAPABILITY_LIMITS,
  capabilityHasNearest,
  partsListedBesideCard,
  SUPPORTED_RATING_UNIT_IDS,
  type CapabilityId,
  type QuestionnaireChatNotDone,
  type QuestionnaireNotDonePart,
} from "@digital-twins/shared-types";
import type { TFunction } from "i18next";

import { ratingScaleUserName } from "./questionnaireIssueCopy";
import { languageName } from "./turnCommandCopy";

/**
 * The builder's words for the questions an objective-panel edit kept although
 * they served only a learning objective it removed (F24): the model only
 * listed them; the line says they stay and where to remove them, in the
 * interface's language, with each question's title a list item under it.
 */
export function removedObjectiveQuestionLines(
  titles: ReadonlyArray<string>,
  t: TFunction<"app">,
): Array<string> {
  return titles.length > 0
    ? [
        t("questionnaire.removedObjectiveQuestions", { count: titles.length }),
        ...titles.map((title) => `- ${title}`),
      ]
    : [];
}

/**
 * Why the builder cannot do a part as asked, from the capability it needs
 * (S4.11), with the limit its registry owner states.
 */
function capabilityWords(capability: CapabilityId, t: TFunction<"app">): string {
  return t(`questionnaire.capability.${capability}`, {
    scales: SUPPORTED_RATING_UNIT_IDS.map(ratingScaleUserName).join(", "),
    max: CAPABILITY_LIMITS.matrixMaxColumns,
    ceiling: CAPABILITY_LIMITS.researchQuestionCeiling,
    languages: CAPABILITY_LIMITS.surveyLanguages.map(languageName).join(", "),
  });
}

/**
 * The builder's words for what a turn did not do (owner decision F16), in the
 * interface's language. The model only named each part; the reviewed line
 * says why, follows what the user acted in (the chat or the objective panel),
 * and names the study's asset panel by the heading that panel shows. Each part
 * is a list item under its line, so the part's own punctuation never matters.
 * The list is the turn's refusals (gate 2026-10-15, L02): a part the
 * questionnaire already does is no refusal, and the reply says what it
 * already does. A part done in its nearest supported version (S4.11) comes
 * first, under a line saying it was not possible as asked. When its capability declares what the
 * nearest version does (C06-1, the registry's typed `nearest`), the part says
 * that, never the capability's gloss of what is not offered; a limit's
 * nearest part keeps the limit as its reason. A part not done that needs a
 * capability the builder lacks says which. The parts listed beside the card
 * the reply carries (`cardAsks`) are `partsListedBesideCard`'s.
 */
export function notDoneLines(
  notDone: QuestionnaireChatNotDone,
  assetPanelHeading: string,
  t: TFunction<"app">,
  cardAsks = false,
): Array<string> {
  const origin =
    notDone.origin === "objective_panel" ? "objectivePanel" : "chat";
  const parts = partsListedBesideCard(notDone.parts, cardAsks);
  const notDoneAt = (where: "asset_panel" | null) =>
    parts.filter(
      (part) =>
        part.status === "not_done" &&
        (part.where === "asset_panel") === (where === "asset_panel"),
    );
  const bullets = (parts: ReadonlyArray<QuestionnaireNotDonePart>) =>
    parts.map((part) => {
      if (!part.capability) return `- ${part.what}`;
      return part.status === "nearest" && capabilityHasNearest(part.capability)
        ? `- ${part.what}: ${t(`questionnaire.capabilityNearest.${part.capability}`)}`
        : `- ${part.what} (${capabilityWords(part.capability, t)})`;
    });
  const group = (
    parts: ReadonlyArray<QuestionnaireNotDonePart>,
    line: "nearest" | "panel" | "none",
    panel?: string,
  ) =>
    parts.length > 0
      ? [
          t(`questionnaire.notDone.${origin}.${line}`, {
            count: parts.length,
            panel,
          }),
          ...bullets(parts),
        ]
      : [];
  return [
    ...group(parts.filter((part) => part.status === "nearest"), "nearest"),
    ...group(notDoneAt("asset_panel"), "panel", assetPanelHeading),
    ...group(notDoneAt(null), "none"),
  ];
}
